import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useState } from 'react';
import { RolesTable } from './components/RolesTable';

import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import { useRoles } from '@lmt/unicorn-shared/app/features/roles/selectors';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { QueryFactory } from '@lmt/shared-types';
import { ToggleButton } from '../../common/ui-kit/ToggleButton';
import { RolesWithGroupsTable } from './components/RolesWithGroupsTable';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

export const RoleList: FC = () => {
  const claims = useClaims();
  const i18next = useScopedTranslation('roles.list');
  const forms = useGlobalForms();
  const roles = useRoles();
  const modals = useModal();
  const [isPermissionsView, setPermissionsView] = useState<boolean>(
    claims.permissions.rolesPageWithPermissions,
  );
  const removeRole = useCallback(async (roleId: number) => {
    const isConfirmed = await modals.showDeletionConfirmation({
      text: i18next.t('confirmation_remove_role'),
    });
    if (isConfirmed) {
      try {
        await QueryFactory.RoleClient.removeRole(roleId);
      } catch (error) {
        await modals.showError({ text: i18next.t('error_remove_role') });
      }
    }
  }, []);

  useInsertKeyHandler(forms.openRoleCreating);

  return (
    <div
      className={tableStyles.pageContainer}
      data-test-id="table-page-container"
    >
      <div className={tableStyles.tableToolbar}>
        <TableTitle title={i18next.t('title')} count={roles.length} />
        {claims.permissions.rolesPageWithPermissions && (
          <div style={{ marginRight: '15px' }}>
            <ToggleButton
              name={'permissions-view'}
              checked={isPermissionsView}
              title={'Show permissions'}
              onChange={(e) => setPermissionsView(e.currentTarget.checked)}
            />
          </div>
        )}
        <Button
          title={i18next.t('add_button')}
          color={ButtonColor.Primary}
          onClick={
            isPermissionsView
              ? forms.openRoleCreating
              : forms.openRoleWithGroupsCreating
          }
          iconComponent={<AddIcon />}
          testId={'plus-button'}
        />
      </div>
      <div className={tableStyles.tableContainer}>
        {isPermissionsView ? (
          <RolesTable
            data={roles}
            noResults={false}
            onDelete={removeRole}
            onEdit={forms.openRoleEditing}
          />
        ) : (
          <RolesWithGroupsTable
            data={roles}
            noResults={false}
            onDelete={removeRole}
            onEdit={forms.openRoleWithGroupsEditing}
          />
        )}
      </div>
    </div>
  );
};
